import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from './contexts/AuthContext';
import { ParallaxProvider } from "react-scroll-parallax";
import Layout from "./layout/Layout";
import SimpleLayout from "./layout/SimpleLayout";
import CareersLayout from './components/careers/CareersLayout';
import FallbackLoader from "./components/Loader/FallbackLoader";


const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Solutions = lazy(() => import("./pages/Solutions"));
const SolutionDetails = lazy(() => import("./pages/SolutionDetails"));
const Insights = lazy(() => import("./pages/Insights"));
const Articles = lazy(() => import("./pages/Articles"));
const Whitepapers = lazy(() => import("./pages/Whitepapers"));
const Blogs = lazy(() => import("./pages/Blogs"));
const CustomerStories = lazy(() => import("./pages/CustomerStories"));
const Article = lazy(() => import("./pages/Article"));
const Newsroom = lazy(() => import("./pages/Newsroom"));
const News = lazy(() => import("./pages/News"));
const Announcement = lazy(() => import("./pages/Announcement"));
const Contact = lazy(() => import("./pages/Contact"));
const Error404 = lazy(() => import("./pages/Error404"));
const Careers = lazy(() => import("./pages/Careers"));
const Team = lazy(() => import("./pages/Team"));
const Thankyou = lazy(() => import("./pages/Thankyou"));
const FleetSense = lazy(() => import("./pages/FleetSense"));
const ComplimentingFleetModernization = lazy(() => import("./pages/blogs/ComplimentingFleetModernization"));
const CabInAlerts = lazy(() => import("./pages/CabInAlerts"));
const CabInVision = lazy(() => import("./pages/CabInVision"));
const RoadSense = lazy(() => import("./pages/RoadSense"));
const HappyDriverProgramme = lazy(() => import("./pages/HappyDriverProgramme"));
const EnvisioningFuture = lazy(() => import("./pages/blogs/EnvisioningFuture"));
const KapoorDiesel = lazy(() => import("./pages/stories/KapoorDiesel"));
const TruckDriverSafety = lazy(() => import("./pages/blogs/TruckDriverSafety"));
const LeverageAIS140 = lazy(() => import("./pages/blogs/LeverageAIS140"));
const BLRLogistics = lazy(() => import("./pages/stories/BLRLogistics"));
const TacklingDistractedDriving = lazy(() => import("./pages/blogs/TacklingDistractedDriving"));
const GovernmentRelations = lazy(() => import("./pages/blogs/GovernmentRelations"));
const EducationAndTraining = lazy(() => import("./pages/blogs/EducationAndTraining"));
const IndustryRelations = lazy(() => import("./pages/blogs/IndustryRelations"));
const MarutInsights = lazy(() => import("./pages/MarutInsights"));
const ShooraLifeSavers = lazy(() => import("./pages/ShooraLifeSavers"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const DeveloperAPI = lazy(() => import("./pages/DeveloperAPI"));
const CSR = lazy(() => import("./pages/CSR"));
const FAQsPage = lazy(() => import("./pages/FAQsPage"));
const CabinVisionDSM = lazy(() => import("./pages/blogs/CabinVisionDSM"));
const RoadSenseADAS = lazy(() => import("./pages/blogs/RoadSenseADAS"));

const TermsOfUse = lazy(() => import("./pages/legal/TermsOfUse"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const AccessibilityStatement = lazy(() => import("./pages/legal/AccessibilityStatement"));
const JobsPage = lazy(() => import("./pages/jobs"));
const JobApplicationsPage = lazy(() => import("./pages/job-applications"));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const JobDescription = lazy(() => import('./components/jobs/JobDescription'));
const ApplyPosition = lazy(() => import('./pages/ApplyPosition'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const EventsPage = lazy(()=> import("./pages/Events"));
const EventPage = lazy(()=> import('./pages/Event'));

// import JobApplication from './components/jobs/JobApplication';


const App = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  return (
    <ParallaxProvider>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<FallbackLoader/>}>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route
                path="/solutions/solution-details"
                element={<SolutionDetails />}
              />
              <Route path="/solutions/fleet-sense" element={<FleetSense />} />
              <Route path="/solutions/cabin-alerts" element={<CabInAlerts />} />
              <Route path="/solutions/cabin-vision" element={<CabInVision />} />
              <Route path="/solutions/road-sense" element={<RoadSense />} />
              <Route
                path="/solutions/marut-insights"
                element={<MarutInsights />}
              />
              <Route
                path="/solutions/shoora-life-savers"
                element={<ShooraLifeSavers />}
              />
              <Route
                path="/solutions/happy-driver-programme"
                element={<HappyDriverProgramme />}
              />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/articles" element={<Articles />} />
              <Route path="/insights/blogs" element={<Blogs />} />
              <Route path="/insights/customer-stories" element={<CustomerStories />} />
              <Route path="/insights/whitepapers" element={<Whitepapers />} />
              <Route path="/insights/article" element={<Article />} />
              <Route
                path="/insights/blog/complimenting-fleet-modernization-with-road-safety"
                element={<ComplimentingFleetModernization />}
              />
              <Route
                path="/insights/blog/envisioning-the-future-of-mobility-with-shoora"
                element={<EnvisioningFuture />}
              />
              <Route
                path="/insights/blog/truck-driver-safety-tips-a-guide-to-protecting-lives-on-the-road"
                element={<TruckDriverSafety />}
              />
              <Route
                path="/insights/blog/how-shoora-aims-to-leverage-ais-140s-mandate-for-telematics-devices-in-public-transportation-and-commercial-vehicles"
                element={<LeverageAIS140 />}
              />
              <Route
                path="/insights/blog/tackling-distracted-driving-transforming-fleet-safety"
                element={<TacklingDistractedDriving />}
              />
              <Route
                path="/insights/blog/cabin-vision-dsm-cameras-redefining-fleet-safety-and-performance"
                element={<CabinVisionDSM />}
              />
              <Route
                path="/insights/blog/roadsense-adas-cameras-redefining-fleet-safety-and-performance"
                element={<RoadSenseADAS />}
              />
              <Route
                path="/insights/customer-stories/kapoor-diesel-fleet-achieves-60200-accident-free-kilometres-with-shooras-cutting-edge-video-telematics-solutions"
                element={<KapoorDiesel />}
              />
              <Route
                path="/insights/customer-stories/how-shoora-life-savers-helped-blr-logistics-avert-over-7500-accidents"
                element={<BLRLogistics />}
              />
              <Route path="/insights/:slug" element={<Article />} />
              <Route
                path="/government-relations"
                element={<GovernmentRelations />}
              />
              <Route
                path="/education-and-training"
                element={<EducationAndTraining />}
              />
              <Route path="/industry-relations" element={<IndustryRelations />} />
              <Route path="/developer-apis" element={<DeveloperAPI />} />
              <Route path="/csr-and-esg" element={<CSR />} />
              <Route path="/faqs" element={<FAQsPage />} />
              <Route path="/newsroom" element={<Newsroom />} />
              <Route path="/newsroom/:slug" element={<News />} />
              <Route path="/announcements/:slug" element={<Announcement />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:slug" element={<EventPage />} />
              <Route path="/team" element={<Team />} />
              <Route path="/thank-you" element={<Thankyou />} />
              <Route path="/coming-soon" element={<ComingSoon />} />
              <Route path="/404" element={<Error404 />} />            
              <Route path="*" element={<Error404 />} />
            </Route>
            <Route path="/legal" element={<SimpleLayout/>} >
              <Route path="/legal/terms-of-use" element={<TermsOfUse />} index />
              <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal/accessibility-statement" element={<AccessibilityStatement />} />
            </Route>
            <Route path="/careers/jobs" element={<SimpleLayout />} >
              <Route path="/careers/jobs" element={<CareersLayout/>}>
              <Route path="/careers/jobs/positions" element={<JobsPage />} />
              <Route path="/careers/jobs/applications" element={<JobApplicationsPage />} />
              <Route path="/careers/jobs/login" element={<Login />} />
              <Route path="/careers/jobs/register" element={<Register />} />
              <Route path="/careers/jobs/forgot-password" element={<ForgotPassword />} />
              <Route path="/careers/jobs/reset-password" element={<ResetPassword />} />
              <Route path="/careers/jobs/:jobId" element={<JobDescription />} />
              <Route path="/careers/jobs/:jobId/apply" element={<ApplyPosition />} />
              </Route>
            </Route>
            <Route path="/verify-email" element={<VerifyEmail/>} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ParallaxProvider>
  );
};

export default App;
